/**
 * Module registry — drop-in extensibility for built-in column processors.
 *
 * Register new modules here; everything else picks them up automatically.
 */

import { imageFanoutModule } from "@/modules/image-fanout/module.server";
import { variantsModule } from "@/modules/variants/module.server";
import type { Module } from "./types";
import type { Profile } from "../types";

export type { Module, ModuleBadgeTone, ModuleConfigSummary } from "./types";
export {
  listCellModules,
  pickableCellModules,
  cellModule,
  pickById,
  isKnownComputedFn,
  templateStrings,
  computedFns,
  AI_RULE_PLACEHOLDER,
} from "./catalog";
export type {
  CellModule,
  ModuleMeta,
  ModuleCategory,
  ModuleIcon,
  ResolveCtx,
} from "./catalog";

const REGISTRY: readonly Module[] = [imageFanoutModule, variantsModule];

/** All modules, registration order. */
export function listModules(): readonly Module[] {
  return REGISTRY;
}

/** Modules that are doing real work for this profile right now. */
export function activeModulesFor(profile: Profile): Module[] {
  return REGISTRY.filter((m) => m.isActive(profile));
}

/**
 * Find the module (if any) that owns this output column for the given profile.
 * Returns the first match — if two modules both claim a column the earlier-
 * registered one wins (no current overlap; future-proofing).
 */
export function moduleForColumn(
  profile: Profile,
  column: string,
): Module | undefined {
  for (const m of REGISTRY) {
    if (!m.isActive(profile)) continue;
    if (m.ownedColumns(profile).includes(column)) return m;
  }
  return undefined;
}

/**
 * column → Module map, for quick lookups (e.g., the sidebar rendering loop).
 * Only includes columns from currently-active modules.
 */
export function ownedColumnIndex(profile: Profile): Record<string, Module> {
  const out: Record<string, Module> = {};
  for (const m of REGISTRY) {
    if (!m.isActive(profile)) continue;
    for (const col of m.ownedColumns(profile)) {
      if (!(col in out)) out[col] = m;
    }
  }
  return out;
}
